import { Injectable } from '@angular/core';

import { Equipment } from '@models/equipment.model';
import { Notification } from '@models/notification.model';
import { Log } from "ng2-logger";
import { NotificationType } from "@models/enums/notificationType.enum";
import { HubConnection, HubConnectionBuilder } from "@aspnet/signalr";
import { environment } from "../../../environments/environment";
import { Subject } from 'rxjs';

/**
 * Service class for handling CRUD operations on {@link Equipment} objects.
 */
@Injectable()
export class NotificationService {
    _logger = Log.create(NotificationService.name);

    private _connectionUrl = `${environment.baseURL}/${environment.notificationQueue}`;
    private _hubConnection: HubConnection;

    private signalRConnected = new Subject<any>();
    signalRConnectedObservable = this.signalRConnected.asObservable();

    private notificationsQueue = new Subject<Notification>();
    notificationsQueueObservable = this.notificationsQueue.asObservable();

    // Tool requests from lines
    private linesToolRequestsQueue = new Subject<Notification>();
    linesToolRequestsQueueObservable = this.linesToolRequestsQueue.asObservable();

    // Tool requests from facilities
    private facilitiesToolRequestsQueue = new Subject<Notification>();
    facilitiesToolRequestsQueueObservable = this.facilitiesToolRequestsQueue.asObservable();

    // Tool returns
    private toolReturnsQueue = new Subject<Notification>();
    toolReturnsQueueObservable = this.toolReturnsQueue.asObservable();

    // Tool return from facilities
    private facilitiesToolReturnQueue = new Subject<Notification>();
    facilitiesToolReturnsQueueObservable = this.facilitiesToolReturnQueue.asObservable();

    // Milkrun requests (to kidscribe)
    private milkrunRequestQueue = new Subject<Notification>();
    milkrunRequestQueueAsObservable = this.milkrunRequestQueue.asObservable();

    // Milkrun returns (to kidscribe)
    private milkrunReturnQueue = new Subject<Notification>();
    milkrunReturnQueueAsObservable = this.milkrunReturnQueue.asObservable();

    private liftOperatorRequestQueue = new Subject<Notification>();
    liftOperatorRequestQueueAsObservable = this.liftOperatorRequestQueue.asObservable();

    private liftOperatorReturnQueue = new Subject<Notification>();
    liftOperatorReturnQueueAsObservable = this.liftOperatorReturnQueue.asObservable();

    private liftOperatorFacilityRequestQueue = new Subject<Notification>();
    liftOperatorFacilityRequestQueueAsObservable = this.liftOperatorFacilityRequestQueue.asObservable();

    private liftOperatorFacilityReturnQueue = new Subject<Notification>();
    liftOperatorFacilityReturnQueueAsObservable = this.liftOperatorFacilityReturnQueue.asObservable();

    // Service operator
    private serviceOperatorQueue = new Subject<Notification>();
    serviceOperatorQueueAsObservable = this.serviceOperatorQueue.asObservable();

    private newTicketQueue = new Subject<Notification>();
    newTicketQueueAsObservable = this.newTicketQueue.asObservable();

    private updatedNewTicketQueue = new Subject<Notification>();
    updatedNewTicketQueueAsObservable = this.updatedNewTicketQueue.asObservable();

    private updatedStrainTicketQueue = new Subject<Notification>();
    updatedStrainTicketQueueAsObservable = this.updatedStrainTicketQueue.asObservable();

    // Line Setter
    private lineSetterToolRequestQueue = new Subject<Notification>();
    lineSetterToolRequestQueueAsObservable = this.lineSetterToolRequestQueue.asObservable();

    private lineSetterToolReturnQueue = new Subject<Notification>();
    lineSetterToolReturnQueueAsObservable = this.lineSetterToolReturnQueue.asObservable();

    // Selected Plants Change
    private selectedPlantsChanged = new Subject<Notification>();
    selectedPlantsChangedAsObservable = this.selectedPlantsChanged.asObservable();

    // Selected Plants Received From API
    private selectedPlantsReceivedFromAPI = new Subject<Notification>();
    selectedPlantsReceivedFromAPIAsObservable = this.selectedPlantsReceivedFromAPI.asObservable();

    constructor() {
    }

    subscribeToMessageSource() {
        this._hubConnection = new HubConnectionBuilder().withUrl(this._connectionUrl).build();

        this.startSignalRConnection();

        this._hubConnection.onclose(() => {
            setTimeout(() => this.startSignalRConnection(), 5000);
        });

        this._hubConnection.on("OnPublishMessage", (receivedMessage: Notification) => {
            this.processNotification(receivedMessage)
        });
    }

    startSignalRConnection() {
        this._hubConnection.start()
        .then(() => {
            this._logger.info("subscribeToMessageSource:: Connected to SignalR queue");
            this.signalRConnected.next()
        })
        .catch(err => {
            this._logger.error("subscribeToMessageSource:: Could not connect to SignalR queue", err);
            this.startSignalRConnection();
        });
    }

    processNotification(notification: Notification) {
        this.notificationsQueue.next(notification);

        switch (notification.message) {
            // Tool request scenario
            case NotificationType.LINE_TOOL_REQUEST_CREATED:
                this.linesToolRequestsQueue.next(notification);
                break;
            case NotificationType.LINE_TOOL_REQUEST_CANCELED:
                this.linesToolRequestsQueue.next(notification);
                break;
            case NotificationType.LINE_TOOL_REQUEST_APPROVED:
                this.lineSetterToolRequestQueue.next(notification);
                this.liftOperatorRequestQueue.next(notification);
                break;
            case NotificationType.LINE_TOOL_REQUEST_REJECTED:
                this.lineSetterToolRequestQueue.next(notification);
                break;
            case NotificationType.LINE_TOOL_REQUEST_START_APPROVE_PROOCESS:
                this.lineSetterToolRequestQueue.next(notification);
                break;
            case NotificationType.LINE_TOOL_REQUEST_PROCESSED:
                this.lineSetterToolRequestQueue.next(notification);
                this.milkrunRequestQueue.next(notification);
                break;
            case NotificationType.LINE_TOOL_REQUEST_LOAD:
                this.milkrunRequestQueue.next(notification);
                this.milkrunReturnQueue.next(notification);
                break;
            case NotificationType.LINE_TOOL_REQUEST_DELIVERED:
                this.lineSetterToolRequestQueue.next(notification);
                this.milkrunReturnQueue.next(notification);
                break;

            // Tool return scenario
            case NotificationType.LINE_TOOL_RETURN_CREATED:
                this.milkrunReturnQueue.next(notification);
                break;
            case NotificationType.LINE_TOOL_RETURN_DELIVERED:
                this.lineSetterToolReturnQueue.next(notification);
                this.toolReturnsQueue.next(notification);
                this.milkrunRequestQueue.next(notification);
                break;
            case NotificationType.LINE_TOOL_RETURN_APPROVED:
                this.lineSetterToolReturnQueue.next(notification);
                this.liftOperatorReturnQueue.next(notification);
                break;
            case NotificationType.LINE_TOOL_RETURN_CANCELED:
                this.milkrunReturnQueue.next(notification);
                break;

            // Tool request (facilities)
            case NotificationType.FACILITY_TOOL_REQUEST_CREATED:
                this.facilitiesToolRequestsQueue.next(notification);
                break;
            case NotificationType.FACILITY_TOOL_REQUEST_START_APPROVE_PROCESS:
                break;
            case NotificationType.FACILITY_TOOL_REQUEST_APPROVED:
                this.liftOperatorFacilityRequestQueue.next(notification);
                break;
            case NotificationType.FACILITY_TOOL_REQUEST_PROCESSED:
                this.serviceOperatorQueue.next(notification);
                break;
            case NotificationType.FACILITY_RETURN_TOOL_REQUEST_CREATED:
                this.facilitiesToolReturnQueue.next(notification);
                break;
            case NotificationType.FACILITY_RETURN_TOOL_REQUEST_APPROVED:
                this.liftOperatorFacilityReturnQueue.next(notification);
                break;

            // Ticket requests/update scenarios
            case NotificationType.NEW_TICKET:
                this.newTicketQueue.next(notification);
                break;
            case NotificationType.UPDATE_TICKET:
                this.updatedNewTicketQueue.next(notification);
                break;

            // Strain scenarios
            case NotificationType.STRAIN_TICKET_UPDATED:
                this.updatedStrainTicketQueue.next(notification);
                break;

        }
        this._logger.info("processNotification:: Processing", notification);
    }

    emitSelectedPlantsChanged() {
        this.selectedPlantsChanged.next();
    }

    emitSelectedPlantsReceived() {
        this.selectedPlantsReceivedFromAPI.next();
    }
}
